import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchReportSummary } from '../api/endpoints.js';
import { PageHeader } from '../components/PageHeader.js';
import { StatCard } from '../components/StatCard.js';
import { ErrorState, Loading } from '../components/States.js';
import { DataTable, type Column } from '../components/DataTable.js';
import type { ReportOutcome } from '../api/endpoints.js';

function formatHandleTime(seconds: number): string {
  if (!seconds || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function DashboardPage() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const query = useQuery({
    queryKey: ['report-summary', from, to],
    queryFn: () =>
      fetchReportSummary({
        from: from || undefined,
        to: to || undefined,
      }),
  });

  const outcomeColumns = useMemo<Column<ReportOutcome>[]>(
    () => [
      { key: 'disposition', header: 'Disposition', render: (r) => r.disposition.replace(/_/g, ' ') },
      { key: 'count', header: 'Count', render: (r) => r.count },
    ],
    [],
  );

  return (
    <div className="page">
      <PageHeader
        title="Reporting"
        subtitle="Contact center activity and outcomes."
        actions={
          <div className="filter-row">
            <label className="field field--inline">
              <span className="field__label">From</span>
              <input
                className="field__input"
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </label>
            <label className="field field--inline">
              <span className="field__label">To</span>
              <input
                className="field__input"
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </label>
          </div>
        }
      />

      {query.isLoading && <Loading />}
      {query.isError && <ErrorState error={query.error} onRetry={() => query.refetch()} />}

      {query.data && (
        <>
          <section className="stat-grid">
            <StatCard label="Inbound calls" value={query.data.inbound_calls} />
            <StatCard label="Outbound calls" value={query.data.outbound_calls} />
            <StatCard label="Inbound chats" value={query.data.inbound_chats} />
            <StatCard label="Outbound chats" value={query.data.outbound_chats} />
            <StatCard label="Missed" value={query.data.missed} />
            <StatCard label="Abandoned" value={query.data.abandoned} />
            <StatCard
              label="Avg handle time"
              value={formatHandleTime(query.data.avg_handle_time_seconds)}
              hint="mm:ss"
            />
            <StatCard
              label="Summary coverage"
              value={`${Math.round(query.data.summary_coverage_pct)}%`}
              hint="conversations with AI summary"
            />
            <StatCard label="Total conversations" value={query.data.total_conversations} />
          </section>

          <section className="card">
            <h2 className="card__title">Outcomes</h2>
            <DataTable
              columns={outcomeColumns}
              rows={query.data.outcomes}
              rowKey={(r) => r.disposition}
              emptyMessage="No dispositions recorded in this range."
            />
          </section>
        </>
      )}
    </div>
  );
}
